import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputAdornment,
  Box,
} from "@mui/material";
import LoadingSkeleton from './LoadingSkeleton';
import {
  Add,
  ViewModule,
  ViewList,
  Search,
  GridView,
} from "@mui/icons-material";
import { useUser } from "../../context/UserContext";
import { useNavigate, useLocation } from "react-router-dom";
import "../Course/CourseGrid.css";
import "../../App.css";
import EmptyState from './EmptyState';
const ItemGrid = ({
  title,
  description,
  itemType, // 'course' or 'learningPath'
  fetchItems,
  ItemCardComponent,
  ItemListComponent,
  createPermission,
  createPath,
  filterOptions = true,
  authorFilterEnabled = true,
  audianceFilterEnabled = true,
  CustomFilterComponent = null,
  showSortingFilter = true,
  showViewToggle = true,
  embedParams = null,
  useRecommendationStyle = false,
  showSearchField=true,
  context="" ,
  gridExtraItems = null,
  hasExtraSearchResults = false,
  onClearFilters = null,
  hasActiveCustomFilters = false,
}) => {
  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const author_id = queryParams.get("author_id");
  const { user } = useUser();
  const [filtersReady, setFiltersReady] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const observerTarget = useRef(null);
  const loadingRef = useRef(false);
  const navigate = useNavigate();
  const [authors, setAuthors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  // Local input state used for UI input and debouncing before committing to searchTerm
  const [searchInput, setSearchInput] = useState("");  // KT addition
  const [sortOrder, setSortOrder] = useState("id DESC");
  const [selectedAuthor, setSelectedAuthor] = useState(author_id || "");
  const [audienceType, setAudienceType] = useState(3);// '' = both
  const searchTimeoutRef = useRef(null);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem(`${itemType}ViewMode`) || "grid";
  });
  const [skillQuery, setSkillQuery] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    total: 0,
    hasMore: true,
  });

  const canCreate = user.permissions?.includes(createPermission);
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const isAuthorOrAdmin =
  (user?.role_name || '').toLowerCase() === 'author' ||
  (user?.role_name || '').toLowerCase() === 'admin';

  const resetAndSearch = useCallback(() => {
    setItems([]);
    setPagination({
      page: 1,
      limit: 12,
      total: 0,
      hasMore: true,
    });
    loadingRef.current = false;

    setLoading(true); // Set loading to true when starting a new search
  }, []);

  const refreshItems = () => {
    setPagination((prev) => ({
      ...prev,
      page: 1,
      hasMore: true,
    }));
    loadingRef.current = false;
    setLoading(true); // Set loading to true when refreshing
    fetchItemsData();
  };

  const fetchItemsData = useCallback(async () => {
    if (loadingRef.current) return;
    // Only perform search if searchTerm is empty or has at least 3 characters
    if (searchTerm.length > 0 && searchTerm.length < 3) {
      setLoading(false);
      return;
    }
    loadingRef.current = true;
    setLoading(true);

    try {
      const { data } = await fetchItems(
        pagination.page,
        pagination.limit,
        searchTerm,
        sortOrder,
        selectedAuthor,
        author_id ? user?.id : null,
        audienceType
      );

      // const itemsList = itemType === 'all' && data.sortedItems
      // ? data.sortedItems
      // : Array.isArray(data[itemType === 'course' ? 'courses' : 'lpaths'])
      // ? data[itemType === 'course' ? 'courses' : 'lpaths']
      // : [];
      const itemsList = (() => {
        if (itemType === "course") return data.courses || [];
        if (itemType === "learningPath") return data.lpaths || [];
        if (itemType === "certificate") return data.certificates || [];
        if (itemType === "jobaid" || itemType === "job aid") return data.job_aids || [];
        if (itemType === "all") return data.sortedItems || [];
        return [];
      })();


      const paginationData = data.pagination || { total: 0, hasMore: false };

      let newItems = [];

      if (pagination.page === 1) {
        newItems = itemsList.map((item) => ({
          ...item,
          uniqueKey: `${item.id}_${Date.now()}_${Math.random()}`,
        }));
      } else {
        newItems = [...items];
        // Append items on subsequent pages
        const additionalItems = itemsList
          .filter(
            (newItem) =>
              !items.some(
                (existingItem) =>
                  existingItem.id === newItem.id &&
                  existingItem.type === newItem.type
              )
          )
          .map((item) => ({
            ...item,
            uniqueKey: `${item.id}_${Date.now()}_${Math.random()}`,
          }));

        newItems = [...newItems, ...additionalItems];
      }
      setItems(newItems);
      setPagination((prev) => ({
        ...prev,
        total: paginationData.total,
        hasMore: paginationData.hasMore,
      }));
    } catch (error) {
      console.error(`Error fetching ${itemType}s:`, error);
    } finally {
      setTimeout(() => {
        setLoading(false);
        loadingRef.current = false;
      }, 300);
    }
  }, [
    pagination.page,
    pagination.limit,
    searchTerm,
    sortOrder,
    selectedAuthor,
    audienceType,
    author_id,
    user?.id,
    fetchItems,
    itemType,
    //items,
  ]);

  const loadAuthors = async () => {
    if (!authorFilterEnabled) return;

    try {
      // This would need to be adjusted based on your API
      const response = await (itemType === "course"
        ? require("../../services/api").courseService.getAuthors()
        : require("../../services/api").learningPathService.getAuthors());
      setAuthors(response.data || []);
    } catch (error) {
      console.error("Error loading authors:", error);
    }
  };

  useEffect(() => {
    loadAuthors();
    setSearchInput("");    // KT addition
    resetAndSearch();
  }, []);

  useEffect(() => {
    const resetHandler = () => {
      resetAndSearch();
    };

    window.addEventListener("resetItemGrid", resetHandler);

    return () => {
      window.removeEventListener("resetItemGrid", resetHandler);
    };
  }, [resetAndSearch]);

  // Reset filters when route changes
  useEffect(() => {
    setFiltersReady(false);
    setSearchTerm("");
    setSearchInput("");  // KT addition
    setSortOrder("id DESC");
    setSelectedAuthor("");

    if (author_id) {
      setSelectedAuthor(author_id);
    }

    setTimeout(() => {
      resetAndSearch();
      setFiltersReady(true);
    }, 0);
  }, [location.pathname, location.search, resetAndSearch]);

  // Cleanup pending search timeout on unmount // KT addition
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (filtersReady) {
      fetchItemsData();
    }
  }, [pagination.page, fetchItemsData, filtersReady]);

  const handleSortChange = (event) => {
    setSortOrder(event.target.value);
    resetAndSearch();
  };

  const handleAuthorChange = (event) => {
    const value = event.target.value;
    setSelectedAuthor(value);
    resetAndSearch();
  };  

  const handleAudienceChange = (event) => {
    setAudienceType(event.target.value);
    resetAndSearch();
  };

  const handleClearFilters = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setSearchTerm('');
    setSearchInput(''); 
    setSortOrder('id DESC');
    setSelectedAuthor('');
    setAudienceType(3);

    setItems([]);
    setPagination({
      page: 1,
      limit: 12,
      total: 0,
      hasMore: true,
    });

    loadingRef.current = false;
    setLoading(true);
    onClearFilters?.();
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    // setSearchTerm(value);  // KT addition removed
    // update input for immediate UI feedback // KT addition
    setSearchInput(value);

    // clear any pending debounce
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Only commit the search when empty or 3+ characters (preserve current behavior)
    if (value.length === 0 || value.length >= 3) {
      searchTimeoutRef.current = setTimeout(() => {
        // commit search term which will trigger fetch via effect
        setSearchTerm(value);  // KT addition
        window.dispatchEvent(new CustomEvent("itemGridSearchChange", { detail: value }));
        // reset items and pagination to first page for new query
        setItems([]);
        resetAndSearch();
      }, 500);
      // setPagination((prev) => ({ ...prev, page: 1, hasMore: true }));
      //   loadingRef.current = false;
      //   setLoading(true);
      // }, 400);
    }
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
      localStorage.setItem(`${itemType}ViewMode`, newView);
    }
  };

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    };

    const handleIntersection = debounce((entries) => {
      if (
        entries[0].isIntersecting &&
        pagination.hasMore &&
        !loadingRef.current &&
        items.length > 0
      ) {
        setPagination((prev) => ({
          ...prev,
          page: prev.page + 1,
        }));
      }
    }, 200);

    const observer = new IntersectionObserver(handleIntersection, options);
    const currentObserverTarget = observerTarget.current;

    if (currentObserverTarget) {
      observer.observe(currentObserverTarget);
    }

    return () => {
      if (currentObserverTarget) {
        observer.unobserve(currentObserverTarget);
      }
      observer.disconnect();
    };
  }, [pagination.hasMore, items.length, pagination.page]);

  // Determine if we should show "no records found" message
  const showNoRecordsMessage = !loading && items.length === 0 && !hasExtraSearchResults;
  const getNoRecordsTitle = () => {
  if (sortOrder === 'status:retired') {
    return `No retired ${itemType === 'course' ? 'courses' : 'learning paths'}`;
  }

  return `No ${
    itemType === 'course'
      ? 'courses'
      : itemType === 'certificate'
      ? 'certificates'
      : itemType === 'learningPath'
      ? 'learning paths'
      : itemType === 'job aid'
      ? 'job aids'
      : 'records'
  } found`;
};

const getNoRecordsMessage = () => {
  if (searchTerm.length > 0 && searchTerm.length < 3) {
    return `Please enter at least 3 characters to search ${
      itemType === 'course' ? 'courses' : 'learning paths'
    }.`;
  }

  return 'Try adjusting your search criteria or filters.';
};

const hasActiveFilters =
  searchInput.trim() !== '' ||
  searchTerm.trim() !== '' ||
  selectedAuthor !== '' ||
  audienceType !== 3 ||
  sortOrder !== 'id DESC' ||
  hasActiveCustomFilters;

  return (
    <div className="courses-container">
      <div className="courses-header">
        <div>
          <Typography variant="h4" gutterBottom className='page-title'>
              {title}
          </Typography>
          <Typography variant="body2" sx={{ mb: 4 }}>
              {description}
          </Typography>
        </div>
        
      </div>
      {/* <hr /> */}
      {showViewToggle}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between'}}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {filterOptions && (
                <Box className="filter-wrapper">
                <Box
                className="filter-container"
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,

                    }}
              >
                <TextField
                    variant="outlined"
                    placeholder={`${
                      itemType === "course"
                        ? "Courses"
                        : itemType === "learningPath"
                        ? "Learning Paths"
                        : (itemType === "jobaid" || itemType === "job aid")
                        ? "Job Aids / Skills"
                        : itemType === "certificate"
                        ? "Certificate"
                        : "Courses & Paths"
                    }`}
                    // value={searchTerm}
                    onChange={handleSearchChange}
                    value={searchInput}   // KT addition
                    className="pink-filter"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 36,
                        display: "flex",
                        alignItems: "center",
                        paddingRight: "8px",
                      },
                      "& input": {
                        padding: "0 10px",        // no extra top/bottom padding
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="end">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
              {/* <div className="filter-container" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', }}> */}
                {/* If CustomFilterComponent is provided, render it */}
                {CustomFilterComponent && <CustomFilterComponent />}

                {(!(user?.role_name || "").toLowerCase() === "author" && !author_id && authorFilterEnabled) && (
                  <FormControl
                    sx={{
                      minWidth: 200,
                      maxWidth: 250,
                      "& .MuiInputBase-root": {
                        height: 36,
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                      },
                      "& .MuiSelect-select": {
                        padding: "0 10px",
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  >
                    <Select
                      displayEmpty
                      value={selectedAuthor}
                      className="pink-filter"
                      onChange={handleAuthorChange}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            marginTop: "4px",
                            minWidth: "200px",
                            maxWidth: "250px",
                            borderRadius: "8px",
                            boxShadow:
                              "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)",
                          },
                        },
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null,
                      }}
                    >
                      <MenuItem value="">All Authors</MenuItem>
                      {authors.map((author) => (
                        <MenuItem key={author.id} value={author.id}>
                          {author.first_name} {author.last_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Conditionally render sorting filter */}
                {(user?.role_name || '').toLowerCase() === 'author' || (user?.role_name || '').toLowerCase() === 'admin' ? (
                  <FormControl
                  sx={{
                    minWidth: 200,
                    maxWidth: 250,
                    "& .MuiInputBase-root": {
                      height: 36,
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiSelect-select": {
                      padding: "0 10px",
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                  >
                    <Select
                      value={audienceType}
                      className="pink-filter"
                      onChange={handleAudienceChange}
                      displayEmpty
                    >
                      <MenuItem value={3}> Both Audience </MenuItem>
                      <MenuItem value={1}> Internal Audience </MenuItem>
                      <MenuItem value={2}> External Audience </MenuItem>                      
                    </Select>
                  </FormControl>
                ) : null}
                <FormControl
                sx={{
                  minWidth: 200,
                  maxWidth: 250,
                  "& .MuiInputBase-root": {
                    height: 36,
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiSelect-select": {
                    padding: "0 10px",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
                >
                  <Select
                    value={sortOrder}
                    className="pink-filter"
                    onChange={handleSortChange}
                    displayEmpty
                  >
                    <MenuItem value="id DESC">Default</MenuItem>
                    <MenuItem
                      value={`${["course", "certificate"].includes(itemType) ? "name" : "title"} ASC`}
                    >
                      Name A-Z
                    </MenuItem>
                    <MenuItem
                      value={`${["course", "certificate"].includes(itemType) ? "name" : "title"} DESC`}
                    >
                      Name Z-A
                    </MenuItem>
                    <MenuItem value="status:retired">Retired</MenuItem>
                    {isAuthorOrAdmin ? (
                      <MenuItem value="status:draft">Draft</MenuItem>
                    ) : null}
                    <MenuItem value="created_at ASC">Date Created (Oldest)</MenuItem>
                    <MenuItem value="created_at DESC">Date Created (Newest)</MenuItem>
                    <MenuItem value="updated_at DESC">Last Updated</MenuItem>
                  </Select>
                </FormControl>

                
                {/* </div> */}
            </Box>
            </Box>
          )}
          {hasActiveFilters && (
            <Button
              variant="text"
              size="small"
              onClick={handleClearFilters}
            >
              Clear filters
            </Button>
          )}
        </Box>
        <div className="courses-actions">
          {showViewToggle && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewChange}
              aria-label="view mode"
              size="small"
              sx={{ mr: 2 }}
            >
              <ToggleButton value="grid" aria-label="grid view">
                <ViewModule />
              </ToggleButton>
              <ToggleButton value="list" aria-label="list view">
                <ViewList />
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate(createPath)}
              className="add-course-btn"
            >
              Add
            </Button>
          )}
        </div>
      </Box>
      {viewMode === "grid" ? (
        <div className="grid-container">
          {gridExtraItems}
          {
            items.length > 0
              ? items.map((item) => (
                  <ItemCardComponent
                    key={item.uniqueKey}
                    item={item}
                    onItemAction={refreshItems}
                    itemType={itemType}
                  />
                ))
              : showNoRecordsMessage && (
                  <EmptyState
                    title={getNoRecordsTitle()}
                    message={getNoRecordsMessage()}
                    actionLabel={hasActiveFilters ? 'Clear filters' : undefined}
                    onAction={hasActiveFilters ? handleClearFilters : undefined}
                  />
                )
          }
        </div>
      ) : (
        <div className="list-container">
          {
            items.length > 0
              ? items.map((item) => (
                  <ItemListComponent
                    key={item.uniqueKey}
                    item={item}
                    onItemAction={refreshItems}
                    itemType={itemType}
                  />
                ))
              : showNoRecordsMessage && (
                  <EmptyState
                    title={getNoRecordsTitle()}
                    message={getNoRecordsMessage()}
                    actionLabel={hasActiveFilters ? 'Clear filters' : undefined}
                    onAction={hasActiveFilters ? handleClearFilters : undefined}
                  />
                )
          }
        </div>
      )}

      <div ref={observerTarget}>
        {loading && items.length > 0 && <LoadingSkeleton rows={2} height={40} />}
      </div>
    </div>
  );
};

export default ItemGrid;
